import { motion } from 'framer-motion';
import { ArrowLeft, BadgeCheck, BarChart3, CalendarDays, Code2, Database, KeyRound, Radio, Terminal, Trophy, UserCog, Users, Zap } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { NOMINAL_CONTEST_DURATION_LABEL, powerUpRules, TEAM_QUESTION_COUNT, timeRules } from '../lib/arena-rules';
import { errorMessage } from '../lib/callables';
import { formatFaNumber } from '../lib/format';
import { useAuth } from '../providers/AuthProvider';

const queryLines = [
  ['۰۱', 'challenge', ': فروش هر محصول را رتبه‌بندی کنید'],
  ['۰۲', 'SELECT', ' TOP (3) p.Name, SUM(d.LineTotal) AS Revenue'],
  ['۰۳', 'FROM', ' SalesOrderDetail d'],
  ['۰۴', 'JOIN', ' Product p ON p.ProductID = d.ProductID'],
  ['۰۵', 'GROUP BY', ' p.Name'],
  ['۰۶', 'ORDER BY', ' Revenue DESC;'],
] as const;

type LoginMode = 'team' | 'admin';

export function LoginPage() {
  const { loginWithTeamCode, loginAsAdmin } = useAuth();
  const [mode, setMode] = useState<LoginMode>('team');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (code.trim().length < 6) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'admin') await loginAsAdmin(code);
      else await loginWithTeamCode(code);
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy(false);
    }
  };

  return (
    <main className="dbx-aurora relative min-h-screen overflow-hidden text-white">
      <div className="arena-grid data-noise absolute inset-0 opacity-70" />
      <div className="dbx-dots absolute -right-24 top-20 h-96 w-96 rotate-12 opacity-30 [mask-image:radial-gradient(circle,black,transparent_70%)]" />
      <div className="absolute -left-36 top-[8%] size-[560px] rounded-full bg-cyan/15 blur-[120px]" />
      <div className="absolute -bottom-52 right-[18%] size-[520px] rounded-full bg-plum/24 blur-[130px]" />

      <header className="relative z-10 mx-auto flex h-24 max-w-[1440px] items-center justify-between px-7 lg:px-12">
        <BrandLockup inverse />
        <nav className="hidden items-center gap-9 text-sm font-bold text-white/65 lg:flex">
          <span className="border-b-2 border-brand pb-2 text-white">خانه</span>
          <span>مسابقه</span>
          <span>جدول امتیازات</span>
          <span>راهنما</span>
        </nav>
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-white/65">
          <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_12px_#34d399]" />
          آنلاین و زنده
        </div>
      </header>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-6rem)] max-w-[1440px] items-center gap-12 px-7 pb-16 lg:grid-cols-[1fr_.95fr] lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotateY: -5 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative hidden min-h-[560px] lg:block"
        >
          <div className="absolute left-8 top-0 h-44 w-44 rounded-[48px] border border-white/10 bg-white/[.035] shadow-2xl backdrop-blur-xl" />
          <div className="dbx-orb absolute left-20 top-7 h-28 w-36 rounded-[50%]" />
          <div className="dbx-orb absolute left-20 top-20 h-28 w-36 rounded-[50%] opacity-95" />
          <div className="dbx-orb absolute left-20 top-32 h-28 w-36 rounded-[50%] opacity-90" />
          <div className="absolute left-48 top-64 grid size-28 place-items-center rounded-full border border-gold/40 bg-gold/15 text-gold shadow-[0_0_60px_rgba(248,185,49,.22)]">
            <Trophy className="size-14 fill-gold/25" />
          </div>
          <div className="absolute right-12 top-20 rotate-[-7deg] rounded-3xl border border-brand/30 bg-brand/10 p-5 shadow-2xl backdrop-blur-xl">
            <div className="rounded-2xl border border-white/10 bg-[#071225]/90 px-8 py-5 text-5xl font-black text-white">T-SQL</div>
          </div>
          <div className="absolute bottom-12 right-16 w-[520px] overflow-hidden rounded-[28px] border border-white/10 bg-[#071225]/88 shadow-2xl backdrop-blur">
            <div className="flex h-14 items-center justify-between border-b border-white/8 px-5" dir="ltr">
              <div className="flex gap-2"><i className="size-2.5 rounded-full bg-red-400" /><i className="size-2.5 rounded-full bg-amber-400" /><i className="size-2.5 rounded-full bg-emerald-400" /></div>
              <span className="font-mono text-[11px] text-white/35">arena-round.sql</span>
              <Terminal className="size-4 text-cyan" />
            </div>
            <div className="relative px-5 py-7 font-mono text-[13px] leading-8" dir="ltr">
              <motion.div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-brand/12 to-transparent" animate={{ y: [0, 225, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'linear' }} />
              {queryLines.map(([line, keyword, rest], index) => (
                <motion.div key={line} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + index * 0.08 }} className="dbx-code-line grid grid-cols-[28px_1fr]">
                  <span className="select-none text-white/18">{line}</span>
                  <span><b className="font-medium text-cyan">{keyword}</b><span className="text-slate-300">{rest}</span></span>
                </motion.div>
              ))}
            </div>
            <div className="flex h-16 items-center justify-between border-t border-white/8 bg-white/[.025] px-5">
              <div className="flex items-center gap-5 text-xs text-white/45" dir="ltr"><span>T-SQL Query Mode</span><span>score = accuracy × time</span></div>
              <div className="flex items-center gap-2 rounded-lg bg-emerald-400/12 px-3 py-2 text-xs font-bold text-emerald-300"><Code2 className="size-3.5" /> Query کامل ثبت شد</div>
            </div>
          </div>
          <div className="absolute bottom-7 left-12 rounded-3xl border border-white/10 bg-white/[.06] p-5 shadow-xl backdrop-blur-xl">
            <Database className="size-8 text-cyan" />
            <b className="mt-3 block">چالش واقعی دیتابیس</b>
            <span className="mt-1 block text-xs text-white/45">بدون اجرای Query؛ ارزیابی بر اساس ساختار پاسخ.</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.2, 0.8, 0.2, 1] }}
          className="justify-self-end"
        >
          <span className="mb-5 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[.06] px-4 py-2 text-xs font-black text-cyan">
            <span className="size-2 rounded-full bg-brand shadow-[0_0_14px_#2563ff]" /> رقابت ملی
          </span>
          <h1 className="text-5xl font-black leading-[1.16] tracking-tight md:text-7xl">
            مسابقه دیتابینکس
            <br />
            <span className="text-brand">T-SQL</span>
          </h1>
          <p className="mt-6 max-w-xl text-xl leading-9 text-slate-200">
            به چالش بکشید، تحلیل کنید، پیروز شوید.
          </p>
          <p className="mt-4 max-w-xl text-sm leading-8 text-slate-400">
            هر تیم با کد مسابقه وارد می‌شود، {formatFaNumber(TEAM_QUESTION_COUNT)} سؤال T-SQL می‌گیرد و باید Query کامل بنویسد. امتیاز از ترکیب دقت Query و سرعت پاسخ محاسبه می‌شود.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-5 text-sm text-white/60">
            <span className="inline-flex items-center gap-2"><Radio className="size-4" /> آنلاین و زنده</span>
            <span className="inline-flex items-center gap-2"><CalendarDays className="size-4" /> مسیر {formatFaNumber(TEAM_QUESTION_COUNT)} سؤالی</span>
            <span className="inline-flex items-center gap-2"><Users className="size-4" /> تیم‌های ۲ تا ۳ نفره</span>
          </div>

          <form onSubmit={(event) => void handleLogin(event)} className="dbx-glass mt-9 max-w-xl rounded-[28px] p-5">
            <div className="mb-4 grid grid-cols-2 rounded-2xl border border-white/10 bg-white/[.04] p-1">
              <button type="button" onClick={() => setMode('team')} className={`flex h-11 items-center justify-center gap-2 rounded-xl text-xs font-black transition ${mode === 'team' ? 'bg-brand text-white shadow-[0_12px_30px_rgba(37,99,255,.32)]' : 'text-white/55 hover:text-white'}`}>
                <KeyRound className="size-4" />
                تیم
              </button>
              <button type="button" onClick={() => setMode('admin')} className={`flex h-11 items-center justify-center gap-2 rounded-xl text-xs font-black transition ${mode === 'admin' ? 'bg-white text-ink' : 'text-white/55 hover:text-white'}`}>
                <UserCog className="size-4" />
                مدیر
              </button>
            </div>
            <label className="block">
              <span className="mb-2 block text-xs font-bold text-white/55">{mode === 'admin' ? 'کد مدیر مسابقه' : 'کد تیم'}</span>
              <Input
                dir="ltr"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder={mode === 'admin' ? 'DBX-ADMIN-7429' : 'DBX-1234-ABCD-EF'}
                className="h-14 rounded-2xl border-white/10 bg-white text-center font-mono text-lg font-black tracking-[0.2em] text-ink"
                autoComplete="one-time-code"
              />
            </label>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <Button size="lg" className="min-w-44 shadow-[0_14px_40px_rgba(37,99,255,.30)]" disabled={busy || code.trim().length < 6} type="submit">
                {busy ? 'در حال بررسی…' : mode === 'admin' ? 'رفتن به اتاق کنترل' : 'ورود به میدان'}
                <ArrowLeft className="size-4" />
              </Button>
              <span className="flex items-center gap-2 text-xs text-white/50"><BadgeCheck className="size-4" /> کد تیم شما را به همان جای قبلی مسابقه برمی‌گرداند</span>
            </div>
            {error && <p role="alert" className="mt-4 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-200">{error}</p>}
          </form>

          <div className="mt-8 grid gap-3 sm:grid-cols-4">
            <FeatureCard icon={<Zap />} title="نتایج لحظه‌ای" text="تابلو و امتیاز زنده" />
            <FeatureCard icon={<Radio />} title="رقابت زنده" text="وضعیت تیم‌ها آنلاین" />
            <FeatureCard icon={<Users />} title="تیمی و حرفه‌ای" text="۲ تا ۳ نفر در هر تیم" />
            <FeatureCard icon={<BarChart3 />} title="امتیازدهی هوشمند" text="دقت Query و زمان پاسخ" />
          </div>
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[.045] p-4">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/55">
              <b className="text-white">زمان اسمی کل {NOMINAL_CONTEST_DURATION_LABEL} دقیقه</b>
              {timeRules.map((item) => <span key={item.label}>{item.label}: {item.value}</span>)}
            </div>
            <p className="mt-3 text-[11px] text-white/45">جایزه‌ها: {powerUpRules.map((item) => item.title).join('، ')}؛ توضیح کامل بعد از ورود نمایش داده می‌شود.</p>
          </div>
        </motion.div>
      </section>
    </main>
  );
}

function FeatureCard({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[.055] p-4">
      <span className="mb-4 grid size-11 place-items-center rounded-xl bg-brand/15 text-cyan">{icon}</span>
      <b className="block text-sm">{title}</b>
      <span className="mt-2 block text-xs leading-6 text-white/45">{text}</span>
    </div>
  );
}
